import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  type ReactNode,
} from "react";

import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { createPortal } from "react-dom";

import TabsComponent from "../Tab";

import "./style.scss";

type DropdownSelectorOption = {
  label: string;
  value: string | null;
  icon?: ReactNode;
  iconClassName?: string;
  /** Shown in the menu but not selectable. */
  disabled?: boolean;
};

interface DropdownSelectorProps {
  label: string;
  selected: string | null;
  options: DropdownSelectorOption[];
  onSelect: (value: string) => void;
  variant?: any;
  enableScroll?: boolean;
  maxHeight?: string;
  /** Smaller trigger + menu text (e.g. My Accounts filters). */
  compact?: boolean;
  /** Optional icon shown on the trigger (e.g. download). */
  icon?: ReactNode;
  /** `button` is purple; `field` is a white form control; default is tab/segmented. */
  triggerStyle?: "segmented" | "button" | "field";
  /** Filter the menu by label. Other dropdowns stay as they are. */
  searchable?: boolean;
  searchPlaceholder?: string;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  hideLabel?: boolean;
  selectedLabelPrefix?: string;
}

const DropdownSelector: React.FC<DropdownSelectorProps> = ({
  label,
  selected,
  options,
  onSelect,
  variant = "segmented",
  enableScroll = false,
  maxHeight = "240px",
  compact = false,
  icon,
  triggerStyle = "segmented",
  searchable = false,
  searchPlaceholder = "Search…",
  loading = false,
  disabled = false,
  fullWidth = false,
  className = "",
  hideLabel = false,
  selectedLabelPrefix = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, minWidth: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);
  const isDisabled = loading || disabled;

  const toggleMenu = useCallback(() => {
    if (isDisabled) return;
    setIsOpen(prev => !prev);
  }, [isDisabled]);

  const getDisplayLabel = () => {
    const option = options.find(opt => opt.value === selected);
    return option ? `${selectedLabelPrefix}${option.label}` : selected || label;
  };

  const updateMenuPosition = useCallback(() => {
    const trigger =
      triggerRef.current ||
      (containerRef.current?.querySelector(
        "button, .input-toggle"
      ) as HTMLElement | null) ||
      containerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuWidth = searchable
      ? Math.max(rect.width, 280)
      : Math.max(rect.width, menuEl?.offsetWidth ?? rect.width);
    const menuHeight = menuEl?.offsetHeight ?? 200;
    const gap = 4;
    const padding = 8;

    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - padding) {
      const above = rect.top - menuHeight - gap;
      if (above >= padding) {
        top = above;
      }
    }

    let left = triggerStyle === "field" ? rect.left : rect.right - menuWidth;
    if (searchable) left = rect.right - menuWidth;
    left = Math.min(left, window.innerWidth - menuWidth - padding);
    left = Math.max(padding, left);

    setMenuPos({
      top,
      left,
      minWidth: searchable ? menuWidth : rect.width,
    });
  }, [searchable, triggerStyle]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    const frame = requestAnimationFrame(() => {
      updateMenuPosition();
      if (
        searchable &&
        searchRef.current &&
        document.activeElement !== searchRef.current
      ) {
        searchRef.current.focus();
      }
      const list = listRef.current;
      const selectedEl = list?.querySelector(
        "[data-dropdown-selected=true]"
      ) as HTMLElement | null;
      if (searchable && list && selectedEl) {
        const top = selectedEl.offsetTop;
        const bottom = top + selectedEl.offsetHeight;
        if (top < list.scrollTop) list.scrollTop = top;
        else if (bottom > list.scrollTop + list.clientHeight) {
          list.scrollTop = bottom - list.clientHeight;
        }
      } else {
        selectedEl?.scrollIntoView({ block: "nearest" });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen, updateMenuPosition, options.length, selected, searchable]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      closeMenu();
    };

    const timeoutId = window.setTimeout(() => {
      document.addEventListener("mousedown", handlePointerDown);
    }, 0);

    const onReposition = () => updateMenuPosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [isOpen, updateMenuPosition, closeMenu]);

  const menuItemClass = compact
    ? "dropdown-selector-menu__item px-3 py-1.5 text-xs text-gray-700"
    : "dropdown-selector-menu__item px-4 py-2";

  const needle = query.trim().toLowerCase();
  const visibleOptions =
    searchable && needle
      ? options.filter(function (opt) {
          return opt.label.toLowerCase().includes(needle);
        })
      : options;

  const menu = isOpen
    ? createPortal(
        <div
          ref={menuRef}
          className={`dropdown-selector-menu fixed z-[10000] bg-white border border-gray-200 rounded-lg shadow-lg ${
            searchable
              ? "dropdown-selector-menu--searchable"
              : `whitespace-nowrap ${
                  enableScroll
                    ? "overflow-y-auto scrollbar-hide"
                    : "overflow-y-auto"
                }`
          }`}
          style={{
            top: menuPos.top,
            left: menuPos.left,
            minWidth: menuPos.minWidth,
            width: searchable ? menuPos.minWidth : undefined,
            maxHeight: enableScroll
              ? maxHeight
              : "min(320px, calc(100vh - 16px))",
          }}
        >
          {searchable ? (
            <div className="dropdown-selector-menu__search-wrap">
              <input
                ref={searchRef}
                type="search"
                value={query}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="dropdown-selector-menu__search"
                onChange={function (event) {
                  setQuery(event.target.value);
                }}
                onKeyDown={function (event) {
                  if (event.key === "Escape") closeMenu();
                }}
              />
            </div>
          ) : null}
          <div
            ref={listRef}
            className={
              searchable
                ? "dropdown-selector-menu__list scrollbar-hide"
                : "contents"
            }
          >
            {visibleOptions.length ? (
              visibleOptions.map(opt => {
                const isSelected = opt.value === selected;
                const isDisabled = Boolean(opt.disabled);
                return (
                  <div
                    key={String(opt.value)}
                    data-dropdown-selected={isSelected ? "true" : undefined}
                    aria-disabled={isDisabled ? "true" : undefined}
                    className={`${menuItemClass}${
                      isDisabled
                        ? " dropdown-selector-menu__item--disabled"
                        : " cursor-pointer hover:bg-gray-100"
                    }${
                      isSelected && !isDisabled
                        ? " dropdown-selector-menu__item--selected"
                        : ""
                    }`}
                    onMouseDown={event => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (isDisabled) return;
                      onSelect(opt.value || "");
                      closeMenu();
                    }}
                  >
                    {opt.icon ? (
                      <span
                        className={`dropdown-selector-menu__icon ${opt.iconClassName || ""}`}
                      >
                        {opt.icon}
                      </span>
                    ) : null}
                    <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                    {isSelected && !isDisabled ? (
                      <Check size={compact ? 14 : 16} strokeWidth={3} />
                    ) : null}
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  const chevron = isOpen ? (
    <ChevronUp size={compact ? 14 : 16} className="flex-shrink-0" />
  ) : (
    <ChevronDown size={compact ? 14 : 16} className="flex-shrink-0" />
  );

  const triggerLabel = (
    <span
      className={`flex items-center gap-1.5 ${
        compact ? "text-xs font-medium" : ""
      }`}
    >
      {icon ? (
        <span className="dropdown-selector-trigger__icon flex-shrink-0">
          {icon}
        </span>
      ) : null}
      <span>{getDisplayLabel()}</span>
      {chevron}
    </span>
  );

  return (
    <div
      className={`filter-dropdown relative ${fullWidth ? "w-full" : ""} ${className}`.trim()}
      ref={containerRef}
    >
      {triggerStyle === "field" && !hideLabel ? (
        <div className="mb-1 text-sm font-medium text-gray-700">{label}</div>
      ) : null}
      {triggerStyle === "button" ? (
        <button
          type="button"
          ref={triggerRef}
          className={`dropdown-selector-trigger--button ${
            isOpen ? "dropdown-selector-trigger--button-open" : ""
          } ${loading ? "dropdown-selector-trigger--button-loading" : ""}`}
          disabled={isDisabled}
          onClick={toggleMenu}
        >
          {triggerLabel}
        </button>
      ) : triggerStyle === "field" ? (
        <button
          type="button"
          ref={triggerRef}
          className={`dropdown-selector-trigger--field ${
            isOpen ? "dropdown-selector-trigger--field-open" : ""
          } ${fullWidth ? "dropdown-selector-trigger--field-full" : ""} ${
            loading ? "dropdown-selector-trigger--field-loading" : ""
          }`}
          disabled={isDisabled}
          onClick={toggleMenu}
          aria-label={label}
        >
          <span className="dropdown-selector-trigger--field-label">
            {getDisplayLabel()}
          </span>
          {chevron}
        </button>
      ) : (
        <div className="input-toggle">
          <TabsComponent
            tabs={[
              {
                id: label.toLowerCase(),
                label: triggerLabel,
              },
            ]}
            activeTab={null}
            onTabChange={toggleMenu}
            variant={variant}
            className="whitespace-nowrap"
          />
        </div>
      )}
      {menu}
    </div>
  );
};

export default DropdownSelector;
